import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { toast } from "sonner";
import {
  Clock,
  CheckCircle2,
  ArrowLeft,
  Sparkles,
  Loader2,
  Mail,
  LayoutDashboard,
  BookOpen,
  ShieldCheck,
} from "lucide-react";

/**
 * Página del estado de una consultoría enviada · rediseño premium.
 * - status="submitted": hero verde + countdown dorado + timeline vivo + CTAs.
 * - status="ready"/"generating": redirige al informe.
 * Polling cada 8s.
 */
export default function ConsultingWaiting() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const r = await api.get(`/consulting/tests/${testId}`);
        setTest(r.data);
        const st = r.data.status;
        // Redirección inteligente según estado de la consultoría:
        // - draft → seguir/retomar el cuestionario
        // - generating/ready/failed → ir al informe (que gestiona sus propios estados)
        // - submitted → quedarse aquí (waiting) y hacer polling
        if (st === "draft") {
          navigate(`/consulting/test/${testId}`, { replace: true });
          return;
        }
        if (st === "generating" || st === "ready" || st === "generating_failed") {
          navigate(`/consulting/${testId}/report`, { replace: true });
          return;
        }
        if (st === "submitted") pollRef.current = setTimeout(load, 8000);
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.error("Consultoría no encontrada");
          navigate("/consulting", { replace: true });
          return;
        }
        toast.error("No pudimos cargar el estado");
      }
    }
    load();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [testId, navigate]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Cálculos temporales
  const { timeStr, elapsedPct, submittedAt, readyAt } = useMemo(() => {
    if (!test) return { timeStr: "…", elapsedPct: 0, submittedAt: null, readyAt: null };
    const submittedIso = test.submitted_at || test.updated_at || test.created_at;
    const readyIso = test.report_ready_at;
    const submitted = submittedIso ? new Date(submittedIso).getTime() : Date.now();
    const ready = readyIso ? new Date(readyIso).getTime() : submitted + 24 * 3600 * 1000;
    const total = Math.max(1, ready - submitted);
    const elapsed = Math.min(total, Math.max(0, now - submitted));
    const msLeft = Math.max(0, ready - now);
    const hours = Math.floor(msLeft / 3600000);
    const mins = Math.floor((msLeft % 3600000) / 60000);
    const secs = Math.floor((msLeft % 60000) / 1000);
    const str = hours > 0 ? `${hours}h ${mins}m` : mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    return { timeStr: str, elapsedPct: (elapsed / total) * 100, submittedAt: submitted, readyAt: ready };
  }, [test, now]);

  const userEmail = getUser()?.email || "";

  // Timeline: se completa según % transcurrido
  const timeline = useMemo(() => {
    const steps = [
      { pct: 0, title: "Cuestionario recibido", description: "Test guardado en tu área privada", icon: CheckCircle2 },
      { pct: 8, title: "Analizando tus respuestas", description: "Extrayendo señales por área y contexto", icon: Sparkles },
      { pct: 25, title: "Contextualizando con tu sector", description: "Cruzando patrones y benchmark del rango de empresa", icon: Sparkles },
      { pct: 50, title: "Redactando bloques del informe", description: "7 áreas · análisis específico por cada bloque", icon: Sparkles },
      { pct: 80, title: "Generando checklist personalizado", description: "30 acciones ordenadas por horizonte (30/90/180 días)", icon: Sparkles },
      { pct: 100, title: "Informe listo", description: "Recibirás un email cuando esté disponible", icon: CheckCircle2 },
    ];
    return steps.map((s) => ({ ...s, done: elapsedPct >= s.pct, active: elapsedPct >= s.pct && elapsedPct < (s.pct + 15) }));
  }, [elapsedPct]);

  if (!test) {
    return (
      <div className="min-h-screen py-12 px-4 md:px-6" style={{ background: "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <div className="skeleton-pulse h-4 w-32 rounded mb-8" />
          <div className="skeleton-pulse rounded-3xl h-72 mb-6" />
          <div className="skeleton-pulse rounded-3xl h-96" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
      data-testid="consulting-waiting"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/consulting")}
          className="text-sm font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-6 md:mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" /> Volver a mis consultorías
        </button>

        {/* Hero premium */}
        <div
          className="relative rounded-3xl p-6 md:p-8 mb-6 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
            boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
          }}
        >
          <span
            aria-hidden
            className="absolute -top-8 -right-8 pointer-events-none"
            style={{
              width: 220,
              height: 220,
              background:
                "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%)," +
                "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)",
            }}
          />
          <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />

          <div className="relative flex items-start gap-5 flex-wrap">
            <div
              className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", boxShadow: "0 8px 22px -6px rgba(201,168,76,0.55)" }}
            >
              <Clock className="w-7 h-7 md:w-8 md:h-8" style={{ color: "#0b3d2e" }} strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                Consultoría en marcha
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
                Tu informe está{" "}
                <span
                  className="whitespace-nowrap"
                  style={{
                    background: "linear-gradient(90deg, #E5C874 0%, #C9A84C 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  generándose
                </span>
              </h1>
              <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.78)" }}>
                {test.company_name} · Procesando tus respuestas cruzadas con benchmarks del sector.
              </p>
              {userEmail && (
                <div className="mt-4 pt-3 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ borderTop: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
                  <span className="inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" style={{ color: "#C9A84C" }} />
                    Te avisaremos a <span style={{ color: "#E5C874", fontWeight: 600 }}>{userEmail}</span>
                  </span>
                  <span className="text-white/25">·</span>
                  <span>Puedes cerrar esta pestaña sin problema</span>
                </div>
              )}
            </div>
          </div>

          {/* Countdown grande */}
          <div className="relative mt-6 md:mt-8 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>Tiempo estimado</div>
              <div className="font-display text-5xl md:text-6xl font-bold tabular-nums mt-1" style={{ color: "#E5C874", letterSpacing: "-0.03em" }} data-testid="consulting-countdown">
                {timeStr}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>Progreso</div>
              <div className="font-display text-2xl font-bold tabular-nums mt-1" style={{ color: "#ffffff" }}>{Math.round(elapsedPct)}%</div>
            </div>
          </div>
          <div className="relative mt-3 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.12)" }}>
            <div
              className="h-full transition-all duration-1000"
              style={{
                width: `${elapsedPct}%`,
                background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                boxShadow: "0 0 10px rgba(201,168,76,0.55)",
              }}
            />
          </div>
        </div>

        {/* Timeline vivo */}
        <div
          className="rounded-3xl p-6 md:p-8 mb-6 relative overflow-hidden"
          style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)" }}
          data-testid="consulting-waiting-timeline"
        >
          <div className="flex items-center gap-3 mb-6">
            <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>
              Qué está pasando ahora
            </div>
          </div>

          <ol className="relative space-y-4">
            {timeline.map((s, idx) => {
              const Icon = s.icon;
              return (
                <li key={idx} className="relative flex gap-4 items-start" data-testid={`consulting-waiting-step-${idx}`}>
                  {idx < timeline.length - 1 && (
                    <span
                      aria-hidden
                      className="absolute left-[15px] top-8 w-0.5 h-full"
                      style={{ background: s.done ? "linear-gradient(180deg, #0F6E56 0%, #C9A84C 100%)" : "#e2e8f0" }}
                    />
                  )}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 transition-all"
                    style={{
                      background: s.done ? "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" : "#ffffff",
                      boxShadow: s.active
                        ? "0 0 0 4px rgba(201,168,76,0.20), 0 4px 10px -2px rgba(11,61,46,0.35)"
                        : s.done
                        ? "0 4px 10px -2px rgba(11,61,46,0.25)"
                        : "0 0 0 1px #e2e8f0",
                    }}
                  >
                    {s.done ? (
                      <Icon className="w-4 h-4" style={{ color: "#E5C874" }} strokeWidth={2.5} />
                    ) : s.active ? (
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#0F6E56" }} />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div
                      className="font-display font-semibold text-sm md:text-base leading-tight"
                      style={{ color: s.done || s.active ? "#0b3d2e" : "#94a3b8" }}
                    >
                      {s.title}
                      {s.active && <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }}>Ahora</span>}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: s.done || s.active ? "#64748b" : "#cbd5e1" }}>{s.description}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* CTAs alternativos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-testid="consulting-waiting-ctas">
          {[
            { icon: LayoutDashboard, title: "Volver al Dashboard", desc: "Sigue con otros diagnósticos", to: "/dashboard" },
            { icon: BookOpen, title: "Explorar consultorías", desc: "Revisa tus otros informes", to: "/consulting" },
            { icon: ShieldCheck, title: "Prepara a tu equipo", desc: "Guía de kickoff PDF (próximamente)", to: null },
          ].map((c, i) => {
            const Icon = c.icon;
            const disabled = !c.to;
            return (
              <button
                key={i}
                disabled={disabled}
                onClick={() => c.to && navigate(c.to)}
                className="text-left rounded-2xl p-4 md:p-5 transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0 disabled:cursor-default"
                style={{
                  background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
                  boxShadow: "0 10px 24px -18px rgba(11,61,46,0.28), 0 0 0 1px rgba(11,61,46,0.06)",
                }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(15,110,86,0.08) 100%)" }}>
                  <Icon className="w-5 h-5" style={{ color: "#0F6E56" }} strokeWidth={2} />
                </div>
                <div className="font-display text-sm font-bold text-slate-900">{c.title}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{c.desc}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
